const FETCH_TIMEOUT_MS = 5_000;

/** @returns The deployed minimum, or `undefined` when the server can't be reached. */
export async function fetchMinClientVersion(): Promise<string | undefined> {
    try {
        // Not /config.json: the service worker serves that from cache when offline, and a stale
        // minimum must never lock out a client that can't reach the server.
        const res = await fetch("/min-client-version", {
            cache: "no-store",
            signal: AbortSignal.timeout(FETCH_TIMEOUT_MS),
        });
        if (!res.ok) {
            return undefined;
        }
        const config: unknown = await res.json();
        if (typeof config === "object" && config !== null && "minClientVersion" in config) {
            return typeof config.minClientVersion === "string" ? config.minClientVersion : undefined;
        }
        return "";
    } catch {
        return undefined;
    }
}

/**
 * @returns The newer version once it is installed and waiting, or `undefined` when none is
 * coming: the update check found nothing, or the new version failed to install.
 */
export async function findNewerVersion(registration: ServiceWorkerRegistration): Promise<ServiceWorker | undefined> {
    // The browser's own update check on page load has no completion signal; awaiting a check of
    // our own (queued behind it if it's still running) tells us when to look at the result.
    await registration.update().catch(() => undefined);
    return whenInstallSettles(registration);
}

/**
 * Activates a waiting version and reloads into it once it controls the page.
 * Talks to the worker directly: workbox-window misses a worker whose install started before it
 * registered, so its update prompt may never fire.
 */
export function activateAndReload(waiting: ServiceWorker) {
    navigator.serviceWorker.addEventListener("controllerchange", () => window.location.reload(), { once: true });
    waiting.postMessage({ type: "SKIP_WAITING" });
}

function whenInstallSettles(registration: ServiceWorkerRegistration): Promise<ServiceWorker | undefined> {
    // An installing worker is newer than a waiting one left over from an earlier session.
    const installing = registration.installing;
    if (!installing) {
        return Promise.resolve(registration.waiting ?? undefined);
    }
    return new Promise((resolve) => {
        const onStateChange = () => {
            if (installing.state === "installed") {
                installing.removeEventListener("statechange", onStateChange);
                resolve(installing);
            } else if (installing.state === "redundant") {
                installing.removeEventListener("statechange", onStateChange);
                // Also redundant when a newer deploy replaced it mid-install; follow that one.
                resolve(whenInstallSettles(registration));
            }
        };
        installing.addEventListener("statechange", onStateChange);
    });
}
